import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  subscribeToPendingQuestCompletions,
  subscribeToAllQuestCompletions,
  approveQuestCompletion,
  rejectQuestCompletion,
  approveQuestCompletionWithIndividualApprovals,
  generateDownloadURL
} from '../../services/firestoreService';
import { getUsernameById } from './functions';
import { MarkdownDisplay, stripMarkdown, QuestLabel, FilterTabs } from '../shared';
import AdminListItem from './AdminListItem';
import AdminEmptyState from './AdminEmptyState';
import AdminPageLayout from './AdminPageLayout';
import { getLocalizedText } from '../../utils/localizationHelpers';

export default function AdminQuestCompletion({ users }) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [pendingCompletions, setPendingCompletions] = useState([]);
  const [allCompletions, setAllCompletions] = useState([]);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [selectedCompletion, setSelectedCompletion] = useState(null);
  const [selectedRevision, setSelectedRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [individualApprovals, setIndividualApprovals] = useState({});
  const [showIndividualReviewModal, setShowIndividualReviewModal] = useState(false);
  const [currentReviewItem, setCurrentReviewItem] = useState(null);
  const [questDetailsExpanded, setQuestDetailsExpanded] = useState(false);

  // Subscribe to pending completions on mount
  useEffect(() => {
    const unsubscribe = subscribeToPendingQuestCompletions((completions) => {
      setPendingCompletions(completions);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Subscribe to all completions only when viewing non-pending tabs
  useEffect(() => {
    if (filterStatus === 'pending') return;

    const unsubscribe = subscribeToAllQuestCompletions((completions) => {
      setAllCompletions(completions);
    });
    return () => unsubscribe();
  }, [filterStatus]);

  const approvedCompletions = allCompletions.filter(c => c.status === 'approved');
  const rejectedCompletions = allCompletions.filter(c => c.status === 'rejected');

  const getFilteredCompletions = () => {
    switch (filterStatus) {
      case 'pending': return pendingCompletions;
      case 'approved': return approvedCompletions;
      case 'rejected': return rejectedCompletions;
      default: return allCompletions;
    }
  };

  const filteredCompletions = getFilteredCompletions();

  const handleReviewAction = (completion, action) => {
    setSelectedCompletion(completion);
    setReviewAction(action);
    setReviewNotes('');
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedCompletion || !currentUser) return;
    
    setProcessing(true);
    try {
      // Get the selected revision ID
      const currentRevision = selectedCompletion.revisions[selectedRevision] || selectedCompletion;
      const revisionId = currentRevision.id;
      
      if (reviewAction === 'approve') {
        await approveQuestCompletion(revisionId, currentUser.uid, reviewNotes);
      } else {
        await rejectQuestCompletion(revisionId, currentUser.uid, reviewNotes);
      }
      
      setShowReviewModal(false);
      setSelectedCompletion(null);
      setReviewNotes('');
    } catch (error) {
      console.error('Error processing quest completion review:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleIndividualReview = (item, action) => {
    setCurrentReviewItem(item);
    setReviewAction(action);
    setReviewNotes('');
    setShowIndividualReviewModal(true);
  };

  const handleSubmitIndividualReview = async () => {
    if (!currentReviewItem || !currentUser || !selectedCompletion) return;
    
    setProcessing(true);
    try {
      // Update individual approval state
      const newApprovals = {
        ...individualApprovals,
        [currentReviewItem.id]: {
          action: reviewAction,
          notes: reviewNotes,
          reviewedBy: currentUser.uid,
          reviewedAt: Date.now()
        }
      };
      setIndividualApprovals(newApprovals);
      
      setShowIndividualReviewModal(false);
      setCurrentReviewItem(null);
      setReviewNotes('');
    } catch (error) {
      console.error('Error processing individual review:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitAllApprovals = async () => {
    if (!selectedCompletion || !currentUser) return;
    
    setProcessing(true);
    try {
      // Get the selected revision
      const currentRevision = selectedCompletion.revisions[selectedRevision] || selectedCompletion;
      
      // Calculate total XP based on approved items
      let totalXp = 0;
      const approvedItems = [];
      
      // Check main quest approval
      const mainQuestApproval = individualApprovals['main'];
      if (mainQuestApproval?.action === 'approve') {
        totalXp += selectedCompletion.questData.value || 0;
        approvedItems.push('main quest');
      }
      
      // Check side quest approvals
      if (selectedCompletion.questData.sideQuests) {
        selectedCompletion.questData.sideQuests.forEach((sideQuest, index) => {
          const sideQuestApproval = individualApprovals[`side-${index}`];
          if (sideQuestApproval?.action === 'approve') {
            totalXp += sideQuest.value || 0;
            approvedItems.push(`side quest: ${getLocalizedText(sideQuest.title, `Side Quest ${index + 1}`)}`);
          }
        });
      }
      
      // Submit the completion with individual approvals using the selected revision ID
      await approveQuestCompletionWithIndividualApprovals(
        currentRevision.id, 
        currentUser.uid, 
        individualApprovals, 
        totalXp,
        approvedItems.join(', ')
      );
      
      setShowReviewModal(false);
      setSelectedCompletion(null);
      setIndividualApprovals({});
      setReviewNotes('');
    } catch (error) {
      console.error('Error submitting individual approvals:', error);
    } finally {
      setProcessing(false);
    }
  };

  const getApprovalStatus = (itemId) => {
    const approval = individualApprovals[itemId];
    if (!approval) return 'pending';
    return approval.action;
  };

  const getApprovalNotes = (itemId) => {
    const approval = individualApprovals[itemId];
    return approval?.notes || '';
  };

  const handleDownloadFile = async (storagePath, filename) => {
    try {
      const downloadURL = await generateDownloadURL(storagePath);
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = downloadURL;
      link.download = filename || 'proof-file';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const getFileIcon = (fileType, filename) => {
    if (!fileType && !filename) return ICONS.FILE;
    
    const type = (fileType || '').toLowerCase();
    const ext = (filename || '').split('.').pop()?.toLowerCase();
    
    if (type.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return ICONS.IMAGE;
    }
    if (type.includes('pdf') || ext === 'pdf') {
      return ICONS.FILE_PDF;
    }
    if (type.includes('spreadsheet') || type.includes('excel') || ['xlsx', 'xls'].includes(ext)) {
      return ICONS.FILE_EXCEL;
    }
    if (type.includes('document') || ['doc', 'docx'].includes(ext)) {
      return ICONS.FILE_WORD;
    }
    return ICONS.FILE;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <FontAwesomeIcon icon={ICONS.SPINNER} spin />
        <p>Loading quest completions...</p>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title={t('admin.questCompletion.title', 'Completion Review')}
      className="admin-quest-completion"
      headerActions={
        <FilterTabs
          tabs={[
            { id: 'pending', label: t('admin.questCompletion.pending', 'Pending'), count: pendingCompletions.length, icon: ICONS.CLOCK },
            { id: 'approved', label: t('admin.questCompletion.approved', 'Approved'), count: allCompletions.length > 0 ? approvedCompletions.length : undefined, icon: ICONS.CHECK_CIRCLE },
            { id: 'rejected', label: t('admin.questCompletion.rejected', 'Rejected'), count: allCompletions.length > 0 ? rejectedCompletions.length : undefined, icon: ICONS.TIMES_CIRCLE },
            { id: 'all', label: t('common.all', 'All'), count: allCompletions.length > 0 ? allCompletions.length : undefined, icon: ICONS.LIST },
          ]}
          activeTab={filterStatus}
          onTabChange={(tab) => { setFilterStatus(tab); setSelectedCompletion(null); }}
        />
      }
      listContent={
        <>
          {filteredCompletions.length === 0 ? (
            <AdminEmptyState
              icon={filterStatus === 'pending' ? ICONS.CHECK_CIRCLE : ICONS.CLIPBOARD_LIST}
              description={
                filterStatus === 'pending' ? 'No quest completions pending review' :
                filterStatus === 'approved' ? 'No approved completions' :
                filterStatus === 'rejected' ? 'No rejected completions' :
                'No quest completions found'
              }
              hint={filterStatus === 'pending' ? 'Completions will appear here when users submit quest proofs for review.' : undefined}
            />
          ) : (
            filteredCompletions.map(completion => {
              const proofCount = Array.isArray(completion.proofSubmissions) ? completion.proofSubmissions.length : Object.keys(completion.proofSubmissions || {}).length;
              return (
                <AdminListItem
                  key={completion.id}
                  title={getLocalizedText(completion.questData.title, 'Quest')}
                  subtitle={stripMarkdown(getLocalizedText(completion.questData.description, 'No description')).substring(0, 100)}
                  selected={selectedCompletion?.id === completion.id}
                  onClick={() => {
                    setSelectedCompletion(completion);
                    setSelectedRevision(0);
                    setQuestDetailsExpanded(false);
                  }}
                  badges={[
                    <QuestLabel key="xp" type="xp" value={completion.questData.value || 0} size="small" />,
                    filterStatus !== 'pending' && <QuestLabel key="status" type="status" value={completion.status} size="small" />,
                  ].filter(Boolean)}
                  meta={[
                    { icon: ICONS.CLOCK, text: new Date(completion.submittedAt).toLocaleDateString() },
                    { icon: ICONS.USER, text: getUsernameById(completion.userId, users) },
                    { icon: ICONS.LINK, text: `${proofCount} proof${proofCount !== 1 ? 's' : ''}` },
                    completion.revisionCount > 1 && { icon: ICONS.CLOCK, text: `${completion.revisionCount} revision${completion.revisionCount !== 1 ? 's' : ''}` },
                  ].filter(Boolean)}
                />
              );
            })
          )}
        </>
      }
      detailContent={
        selectedCompletion ? (
          <div className="details-content">
              <div className="admin-detail-header">
                  <div className="detail-header-top">
                    <h3>{getLocalizedText(selectedCompletion.questData.title, 'Quest')}</h3>
                    <QuestLabel type="status" value={selectedCompletion.status} size="small" />
                  </div>
                  <div className="detail-header-meta">
                    <span className="detail-meta-item">
                      <FontAwesomeIcon icon={ICONS.USER} />
                      {getUsernameById(selectedCompletion.userId, users)}
                    </span>
                    <span className="detail-meta-item">
                      <FontAwesomeIcon icon={ICONS.CLOCK} />
                      {new Date(selectedCompletion.submittedAt).toLocaleDateString()}
                    </span>
                    <QuestLabel type="xp" value={selectedCompletion.questData.value || 0} size="small" />
                    {selectedCompletion.revisionCount > 1 && (
                      <div className="revision-selector">
                        <select
                          id="revisionSelect"
                          value={selectedRevision}
                          onChange={(e) => setSelectedRevision(parseInt(e.target.value))}
                          className="revision-dropdown"
                        >
                          {selectedCompletion.revisions.map((revision, index) => (
                            <option key={revision.id} value={index}>
                              Rev {selectedCompletion.revisions.length - index} — {new Date(revision.submittedAt).toLocaleDateString()}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="quest-info">
                  {/* Collapsible Quest Details */}
                  <div className={`collapsible-section ${questDetailsExpanded ? 'expanded' : ''}`}>
                    <button
                      className="collapsible-header"
                      onClick={() => setQuestDetailsExpanded(!questDetailsExpanded)}
                    >
                      <h4>
                        <FontAwesomeIcon icon={ICONS.INFO_CIRCLE} />
                        Quest Details
                      </h4>
                      <FontAwesomeIcon
                        icon={ICONS.CHEVRON_DOWN}
                        className="collapsible-chevron"
                      />
                    </button>
                    {questDetailsExpanded && (
                      <div className="collapsible-body">
                        <MarkdownDisplay content={getLocalizedText(selectedCompletion.questData.description, 'No description')} />

                        <div className="quest-meta">
                          <div className="meta-row">
                            <span className="meta-item">
                              <strong>XP Value:</strong> <QuestLabel type="xp" value={selectedCompletion.questData.value || 0} size="small" />
                            </span>
                            <span className="meta-item">
                              <strong>Difficulty:</strong> <QuestLabel type="difficulty" value={selectedCompletion.questData.difficulty || 'medium'} size="small" />
                            </span>
                            <span className="meta-item">
                              <strong>Category:</strong> {selectedCompletion.questData.category || 'General'}
                            </span>
                          </div>
                          <div className="meta-row">
                            <span className="meta-item">
                              <strong>User:</strong> {getUsernameById(selectedCompletion.userId, users)}
                            </span>
                            <span className="meta-item">
                              <strong>User ID:</strong> {selectedCompletion.userId}
                            </span>
                          </div>
                          {selectedCompletion.questData.estimatedTime && (
                            <div className="meta-row">
                              <span className="meta-item">
                                <FontAwesomeIcon icon={ICONS.CLOCK} />
                                <strong>Estimated Time:</strong> {selectedCompletion.questData.estimatedTime}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Side Quests Section */}
                        {selectedCompletion.questData.sideQuests && selectedCompletion.questData.sideQuests.length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <h5 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <FontAwesomeIcon icon={ICONS.LIST} />
                              Side Quests ({selectedCompletion.questData.sideQuests.length})
                            </h5>
                            <div className="side-quests-preview">
                              {selectedCompletion.questData.sideQuests.slice(0, 3).map((sideQuest, index) => (
                                <div key={index} className="side-quest-preview-item">
                                  <QuestLabel type="difficulty" value={sideQuest.difficulty || 'medium'} size="small" />
                                  <div className="side-quest-content">
                                    <div className="side-quest-title">
                                      {getLocalizedText(sideQuest.title, `Side Quest ${index + 1}`)}
                                    </div>
                                    <QuestLabel type="xp" value={sideQuest.value || 0} size="small" />
                                  </div>
                                  {sideQuest.proofRequirements && sideQuest.proofRequirements.length > 0 && (
                                    <div className="side-quest-files">
                                      {sideQuest.proofRequirements.map((req, reqIndex) => (
                                        req.inputType && ['image', 'pdf', 'excel'].includes(req.inputType.toLowerCase()) && (
                                          <span key={reqIndex} className="file-type-indicator" title={`Requires ${req.inputType} file`}>
                                            <FontAwesomeIcon icon={getFileIcon(req.inputType)} />
                                          </span>
                                        )
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {selectedCompletion.questData.sideQuests.length > 3 && (
                                <div className="side-quests-more">
                                  +{selectedCompletion.questData.sideQuests.length - 3} more side quests
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Main Quest Proof Submissions */}
                  <div className="admin-detail-section">
                    <h4>Main Quest Submitted Proof</h4>
                    {(() => {
                      const currentRevision = selectedCompletion.revisions[selectedRevision] || selectedCompletion;
                      return Array.isArray(currentRevision.proofSubmissions) && currentRevision.proofSubmissions.length > 0 ? (
                        <div className="proof-list">
                          {currentRevision.proofSubmissions.map((proof, index) => {
                            const requirement = selectedCompletion.questData.proofRequirements?.[index];
                            const label = requirement ? getLocalizedText(requirement.label, `Proof ${index + 1}`) : (proof.label || `Proof ${index + 1}`);
                            const value = proof.value;
                            const isUrl = typeof value === 'string' && /^https?:\/\//i.test(value);
                            return (
                              <div key={index} className="proof-item">
                                <div className="proof-header">
                                  <h5>{label}</h5>
                                  {requirement?.required && (
                                    <span className="required-badge">Required</span>
                                  )}
                                </div>
                                <div className="proof-content">
                                  {proof.storagePath ? (
                                    <div className="proof-file-display">
                                      <div className="file-info">
                                        <FontAwesomeIcon 
                                          icon={getFileIcon(proof.fileType, value)} 
                                          className="file-icon"
                                        />
                                        <div className="file-details">
                                          <span className="file-name">{value}</span>
                                          {proof.fileSize && (
                                            <span className="file-size">{formatFileSize(proof.fileSize)}</span>
                                          )}
                                        </div>
                                      </div>
                                      <button
                                        className="download-btn"
                                        onClick={() => handleDownloadFile(proof.storagePath, value)}
                                        title="Download file"
                                      >
                                        <FontAwesomeIcon icon={ICONS.DOWNLOAD} />
                                        Download
                                      </button>
                                    </div>
                                  ) : isUrl ? (
                                    <a href={value} target="_blank" rel="noopener noreferrer" className="proof-link">
                                      <FontAwesomeIcon icon={ICONS.EXTERNAL_LINK} />
                                      {value}
                                    </a>
                                  ) : (
                                    <span className="proof-text">{value || '—'}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p>No main quest proof submitted</p>
                      );
                    })()}
                  </div>

                  {/* Side Quest Proof Submissions */}
                  {(() => {
                    const currentRevision = selectedCompletion.revisions[selectedRevision] || selectedCompletion;
                    return currentRevision.sideQuestProofSubmissions && Object.keys(currentRevision.sideQuestProofSubmissions).length > 0 && (
                      <div className="admin-detail-section">
                        <h4>Side Quest Submitted Proof</h4>
                        <div className="side-quest-proof-submissions">
                            {Object.entries(currentRevision.sideQuestProofSubmissions).map(([sideQuestIndex, sideQuestProofs]) => {
                            const sideQuest = selectedCompletion.questData.sideQuests?.[parseInt(sideQuestIndex)];
                            if (!sideQuest || !Array.isArray(sideQuestProofs) || sideQuestProofs.length === 0) {
                              return null;
                            }

                            return (
                              <div key={sideQuestIndex} className="side-quest-proof-submission">
                                <h5 className="side-quest-submission-title">
                                  {getLocalizedText(sideQuest.title, `Side Quest ${parseInt(sideQuestIndex) + 1}`)}
                                  <span className="side-quest-xp">+{sideQuest.value || 0} XP</span>
                                </h5>
                                <div className="side-quest-proof-list">
                                  {sideQuestProofs.map((proof, proofIndex) => {
                                    const requirement = sideQuest.proofRequirements?.[proofIndex];
                                    const label = requirement ? getLocalizedText(requirement.label, `Proof ${proofIndex + 1}`) : (proof.label || `Proof ${proofIndex + 1}`);
                                    const value = proof.value;
                                    const isUrl = typeof value === 'string' && /^https?:\/\//i.test(value);
                                    return (
                                      <div key={proofIndex} className="proof-item">
                                        <div className="proof-header">
                                          <h6>{label}</h6>
                                          {requirement?.required && (
                                            <span className="required-badge">Required</span>
                                          )}
                                        </div>
                                        <div className="proof-content">
                                          {proof.storagePath ? (
                                            <div className="proof-file-display">
                                              <div className="file-info">
                                                <FontAwesomeIcon 
                                                  icon={getFileIcon(proof.fileType, value)} 
                                                  className="file-icon"
                                                />
                                                <div className="file-details">
                                                  <span className="file-name">{value}</span>
                                                  {proof.fileSize && (
                                                    <span className="file-size">{formatFileSize(proof.fileSize)}</span>
                                                  )}
                                                </div>
                                              </div>
                                              <button
                                                className="download-btn"
                                                onClick={() => handleDownloadFile(proof.storagePath, value)}
                                                title="Download file"
                                              >
                                                <FontAwesomeIcon icon={ICONS.DOWNLOAD} />
                                                Download
                                              </button>
                                            </div>
                                          ) : isUrl ? (
                                            <a href={value} target="_blank" rel="noopener noreferrer" className="proof-link">
                                              <FontAwesomeIcon icon={ICONS.EXTERNAL_LINK} />
                                              {value}
                                            </a>
                                          ) : (
                                            <span className="proof-text">{value || '—'}</span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Review info for already-reviewed completions */}
                {selectedCompletion.status !== 'pending' && selectedCompletion.reviewedBy && (
                  <div className="admin-detail-section">
                    <h4>{selectedCompletion.status === 'approved' ? 'Approval' : 'Rejection'} Details</h4>
                    <div className="quest-meta">
                      <div className="meta-row">
                        <span className="meta-item">
                          <strong>Reviewed by:</strong> {getUsernameById(selectedCompletion.reviewedBy, users)}
                        </span>
                        {selectedCompletion.reviewedAt && (
                          <span className="meta-item">
                            <strong>Reviewed at:</strong> {new Date(selectedCompletion.reviewedAt).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {selectedCompletion.reviewNotes && (
                        <div className="meta-row">
                          <span className="meta-item">
                            <strong>Notes:</strong> {selectedCompletion.reviewNotes}
                          </span>
                        </div>
                      )}
                      {selectedCompletion.status === 'approved' && selectedCompletion.xpAwarded !== undefined && (
                        <div className="meta-row">
                          <span className="meta-item">
                            <strong>XP Awarded:</strong> {selectedCompletion.xpAwarded}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Individual Quest/Side Quest Approvals (pending only) */}
                {selectedCompletion.status === 'pending' && <>
                <div className="individual-approvals-section">
                  <h4>Individual Approvals</h4>
                  
                  {/* Main Quest Approval */}
                  <div className="approval-item">
                    <div className="approval-header">
                      <h5>Main Quest: {getLocalizedText(selectedCompletion.questData.title, 'Quest')}</h5>
                      <div className="approval-status">
                        <QuestLabel
                          type="status"
                          value={getApprovalStatus('main') === 'approve' ? 'approved' :
                                 getApprovalStatus('main') === 'reject' ? 'rejected' : 'pending'}
                          size="small"
                        />
                        <span className="xp-value">
                          <FontAwesomeIcon icon={ICONS.COINS} />
                          {selectedCompletion.questData.value || 0} XP
                        </span>
                      </div>
                    </div>
                    {getApprovalNotes('main') && (
                      <p className="approval-notes">{getApprovalNotes('main')}</p>
                    )}
                    <div className="approval-actions">
                      <button 
                        className={`approve-btn ${getApprovalStatus('main') === 'approve' ? 'active' : ''}`}
                        onClick={() => handleIndividualReview({ id: 'main', title: 'Main Quest', xp: selectedCompletion.questData.value || 0 }, 'approve')}
                        disabled={processing}
                      >
                        <FontAwesomeIcon icon={ICONS.CHECK} />
                        Approve Main Quest
                      </button>
                      <button 
                        className={`reject-btn ${getApprovalStatus('main') === 'reject' ? 'active' : ''}`}
                        onClick={() => handleIndividualReview({ id: 'main', title: 'Main Quest', xp: selectedCompletion.questData.value || 0 }, 'reject')}
                        disabled={processing}
                      >
                        <FontAwesomeIcon icon={ICONS.TIMES} />
                        Reject Main Quest
                      </button>
                    </div>
                  </div>

                  {/* Side Quest Approvals */}
                  {selectedCompletion.questData.sideQuests && selectedCompletion.questData.sideQuests.length > 0 && (
                    <div className="side-quest-approvals">
                      <h5>Side Quests</h5>
                      {selectedCompletion.questData.sideQuests.map((sideQuest, index) => (
                        <div key={index} className="approval-item">
                          <div className="approval-header">
                            <h6>{getLocalizedText(sideQuest.title, `Side Quest ${index + 1}`)}</h6>
                            <div className="approval-status">
                              <QuestLabel
                                type="status"
                                value={getApprovalStatus(`side-${index}`) === 'approve' ? 'approved' :
                                       getApprovalStatus(`side-${index}`) === 'reject' ? 'rejected' : 'pending'}
                                size="small"
                              />
                              <span className="xp-value">
                                <FontAwesomeIcon icon={ICONS.COINS} />
                                {sideQuest.value || 0} XP
                              </span>
                            </div>
                          </div>
                          <div className="side-quest-description">
                            <MarkdownDisplay content={getLocalizedText(sideQuest.description, 'No description')} />
                          </div>
                          {getApprovalNotes(`side-${index}`) && (
                            <p className="approval-notes">{getApprovalNotes(`side-${index}`)}</p>
                          )}
                          <div className="approval-actions">
                            <button 
                              className={`approve-btn ${getApprovalStatus(`side-${index}`) === 'approve' ? 'active' : ''}`}
                              onClick={() => handleIndividualReview({ 
                                id: `side-${index}`, 
                                title: getLocalizedText(sideQuest.title, `Side Quest ${index + 1}`), 
                                xp: sideQuest.value || 0 
                              }, 'approve')}
                              disabled={processing}
                            >
                              <FontAwesomeIcon icon={ICONS.CHECK} />
                              Approve
                            </button>
                            <button 
                              className={`reject-btn ${getApprovalStatus(`side-${index}`) === 'reject' ? 'active' : ''}`}
                              onClick={() => handleIndividualReview({ 
                                id: `side-${index}`, 
                                title: getLocalizedText(sideQuest.title, `Side Quest ${index + 1}`), 
                                xp: sideQuest.value || 0 
                              }, 'reject')}
                              disabled={processing}
                            >
                              <FontAwesomeIcon icon={ICONS.TIMES} />
                              Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Submit All Approvals */}
                  <div className="submit-all-approvals">
                    <div className="approval-summary">
                      <h5>Approval Summary</h5>
                      <div className="summary-stats">
                        <span className="approved-count">
                          Approved: {Object.values(individualApprovals).filter(a => a.action === 'approve').length}
                        </span>
                        <span className="rejected-count">
                          Rejected: {Object.values(individualApprovals).filter(a => a.action === 'reject').length}
                        </span>
                        <span className="total-xp">
                          Total XP: {Object.entries(individualApprovals).reduce((total, [key, approval]) => {
                            if (approval.action === 'approve') {
                              if (key === 'main') {
                                return total + (selectedCompletion.questData.value || 0);
                              } else if (key.startsWith('side-')) {
                                const sideIndex = parseInt(key.split('-')[1]);
                                return total + (selectedCompletion.questData.sideQuests[sideIndex]?.value || 0);
                              }
                            }
                            return total;
                          }, 0)}
                        </span>
                      </div>
                    </div>
                    <button 
                      className="submit-all-btn"
                      onClick={handleSubmitAllApprovals}
                      disabled={processing || Object.keys(individualApprovals).length === 0}
                    >
                      {processing ? (
                        <>
                          <FontAwesomeIcon icon={ICONS.SPINNER} spin />
                          Processing...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={ICONS.CHECK} />
                          Submit All Approvals
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Legacy Review Actions (for backward compatibility) */}
                <div className="review-actions legacy">
                  <button
                    className="approve-btn"
                    onClick={() => handleReviewAction(selectedCompletion, 'approve')}
                    disabled={processing}
                  >
                    <FontAwesomeIcon icon={ICONS.CHECK} />
                    Approve All
                  </button>
                  <button
                    className="reject-btn"
                    onClick={() => handleReviewAction(selectedCompletion, 'reject')}
                    disabled={processing}
                  >
                    <FontAwesomeIcon icon={ICONS.TIMES} />
                    Reject All
                  </button>
                </div>
                </>}
          </div>
        ) : (
          <AdminEmptyState
            icon={ICONS.CLIPBOARD_LIST}
            description="Select a quest completion to review"
          />
        )
      }
    >
      {/* Review Modal */}
      {showReviewModal && (
        <div className="modal-overlay">
          <div className="modal review-modal">
            <div className="modal-header">
              <h3>{reviewAction === 'approve' ? 'Approve' : 'Reject'} Quest Completion</h3>
              <button 
                className="close-btn"
                onClick={() => setShowReviewModal(false)}
                disabled={processing}
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
              </button>
            </div>
            <div className="modal-content">
              <div className="review-summary">
                <h4>{getLocalizedText(selectedCompletion?.questData.title, 'Quest')}</h4>
                <p>Are you sure you want to {reviewAction} this quest completion?</p>
              </div>
              
              <div className="review-notes-section">
                <label htmlFor="reviewNotes">
                  {reviewAction === 'approve' ? 'Approval Notes (Optional)' : 'Rejection Reason (Required)'}
                </label>
                <textarea
                  id="reviewNotes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder={reviewAction === 'approve' ? 'Add any notes for the user...' : 'Explain why this submission is being rejected...'}
                  rows="4"
                  disabled={processing}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className={`${reviewAction}-btn`}
                onClick={handleSubmitReview}
                disabled={processing || (reviewAction === 'reject' && !reviewNotes.trim())}
              >
                {processing ? (
                  <>
                    <FontAwesomeIcon icon={ICONS.SPINNER} spin />
                    Processing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={reviewAction === 'approve' ? ICONS.CHECK : ICONS.TIMES} />
                    {reviewAction === 'approve' ? 'Approve' : 'Reject'}
                  </>
                )}
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setShowReviewModal(false)}
                disabled={processing}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Individual Review Modal */}
      {showIndividualReviewModal && (
        <div className="modal-overlay">
          <div className="modal review-modal">
            <div className="modal-header">
              <h3>{reviewAction === 'approve' ? 'Approve' : 'Reject'} {currentReviewItem?.title}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowIndividualReviewModal(false)}
                disabled={processing}
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
              </button>
            </div>
            <div className="modal-content">
              <div className="review-summary">
                <h4>{currentReviewItem?.title}</h4>
                <p>XP Value: {currentReviewItem?.xp || 0}</p>
                <p>Are you sure you want to {reviewAction} this quest/side quest?</p>
              </div>
              
              <div className="review-notes-section">
                <label htmlFor="individualReviewNotes">
                  {reviewAction === 'approve' ? 'Approval Notes (Optional)' : 'Rejection Reason (Required)'}
                </label>
                <textarea
                  id="individualReviewNotes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder={reviewAction === 'approve' ? 'Add any notes for the user...' : 'Explain why this quest/side quest is being rejected...'}
                  rows="4"
                  disabled={processing}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className={`${reviewAction}-btn`}
                onClick={handleSubmitIndividualReview}
                disabled={processing || (reviewAction === 'reject' && !reviewNotes.trim())}
              >
                {processing ? (
                  <>
                    <FontAwesomeIcon icon={ICONS.SPINNER} spin />
                    Processing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={reviewAction === 'approve' ? ICONS.CHECK : ICONS.TIMES} />
                    {reviewAction === 'approve' ? 'Approve' : 'Reject'}
                  </>
                )}
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setShowIndividualReviewModal(false)}
                disabled={processing}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </AdminPageLayout>
  );
} 